import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2 } from 'lucide-react';

import { env } from '@/env';
import { qgammonAuthHeaders } from '@/lib/qgammon/auth';
import { useHasLogtoRole } from '@/hooks/useHasLogtoRole';
import { useStartMatch } from '@/hooks/useStartMatch';
import { useHaptics } from '@/hooks/useHaptics';
import type { Player } from '@qlashgeminiz/qgammon-rules';

/**
 * `/invito/:code` — dove atterra chi apre `t.me/<bot>?startapp=bg_<CODICE>`.
 *
 * ═══ PERCHE' LA ROTTA E' `/invito/<codice>` E NON `/backgammon/match/<id>` ═══
 * 🔑 **Non esiste un match a cui puntare.** L'invito QGammon e' un bearer
 * monouso da 15 minuti, e il match lo CREA l'accettazione (`POST
 * /invites/:code/accept` → `registry.create`). Un link a `/backgammon/match/<id>`
 * o non avrebbe un id da mettere, o ne avrebbe uno a cui l'ospite non ha ancora
 * diritto. E' una decisione di MODELLO, non di naming.
 *
 * ═══ PERCHE' STA FUORI DA `backgammonRoutes` ═══
 * Quelle rotte esistono solo se `featureBackgammonEnabled`, e sono dentro
 * `<RoleGate role="beta">`. Un ospite senza il ruolo, o su una build col flag
 * spento, cadrebbe sul catch-all `*` e verrebbe rimandato alla Hub — cioe'
 * ESATTAMENTE il fallimento silenzioso che il deep link doveva evitare.
 * Qui invece la schermata esiste sempre e dice quale dei casi e'.
 * 📌 [M 06/09] In PRODUZIONE `VITE_FEATURE_BACKGAMMON_ENABLED=false`; su
 * staging e' `true`. Un link di prod oggi non puo' arrivare a una partita, e
 * l'ospite ha diritto di leggerlo invece di indovinarlo.
 *
 * ═══ I RIFIUTI NON SONO CASI LIMITE ═══
 * Sono il comportamento normale di un bearer: chiunque abbia il codice puo'
 * accettarlo, una volta sola, entro 15 minuti. Quindi 404 (scaduto o gia' preso
 * da un altro), 400 `cannot_accept_own`, 409 `too_many_active_matches` (con
 * `who`, che dice CHI dei due e' pieno) sono esiti previsti, e ognuno ha una
 * frase che dice cosa e' successo E cosa si puo' fare. ⛔ Nessuno di questi e'
 * «errore».
 */

interface InvitePreview {
  creator: { username: string; displayName: string; elo: number };
  matchLength: number;
  expiresAt: string;
}

/**
 * ⚠️ `expired` e `gone` sono DUE cause che il server manda con lo STESSO 404.
 * `peekInvite` nullo e `claimInvite` nullo danno entrambi `not_found`: dalla
 * risposta, da sola, non si distinguono. Le distingue la SEQUENZA — v.
 * `refusalFor404`. E' la stessa contromisura che la sezione Limulo usa contro il
 * 424 muto: quando il server non porta la causa, la si ricava da cio' che si e'
 * visto un attimo prima.
 */
type Refusal = 'expired' | 'gone' | 'own' | 'capYou' | 'capCreator' | 'unreachable';

/**
 * Perche' questo invito non c'e' piu'.
 *
 * 🔑 Il caso che rende la distinzione necessaria e' NUOVO col deep link: un
 * codice letto a voce e' quasi sempre «scaduto», ma un LINK inoltrato in un
 * gruppo rende normale «l'ha gia' preso un altro» — e dire «scaduto» a chi ha
 * appena perso una corsa e' dirgli la cosa sbagliata, per giunta suggerendogli
 * di aspettare invece di chiedere un invito nuovo.
 *
 * ⛔ E non si afferma piu' di quel che si sa: preview vista + tempo residuo +
 * accept 404 esclude la scadenza, NON dice chi. Poteva anche essere il creatore
 * che ha annullato (`DELETE /invites/:code` → stesso 404). La copy nomina
 * entrambe le cause invece di sceglierne una che suona meglio.
 */
function refusalFor404(preview: InvitePreview | null, now: number): 'expired' | 'gone' {
  // Mai vista viva: non e' mai esistita, o e' scaduta da prima che arrivassimo.
  if (!preview) return 'expired';
  const expiresAt = Date.parse(preview.expiresAt);
  // Il tempo e' finito mentre l'ospite leggeva: la scadenza e' la causa vera.
  if (!Number.isFinite(expiresAt) || expiresAt <= now) return 'expired';
  // Era viva un attimo fa e adesso non c'e' piu': non e' il tempo.
  return 'gone';
}

/** Stesso alfabeto/lunghezza di `AcceptInviteSheet` e di `lib/startParam`. */
const CODE_RE = /^[A-Z2-9]{10}$/;

export function InviteLanding() {
  const { t } = useTranslation('backgammon');
  const { code: rawCode } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const startMatch = useStartMatch();
  const haptics = useHaptics();
  const isBeta = useHasLogtoRole('beta');

  const code = (rawCode ?? '').toUpperCase();
  const codeLooksValid = CODE_RE.test(code);

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  // La build non ha la sezione, oppure l'utente non ha il ruolo: la chiamata al
  // gameserver non si fa proprio. Chiedere per poi non poter navigare sarebbe
  // bruciare l'invito — che e' MONOUSO — per una partita irraggiungibile.
  const featureOff = !env.featureBackgammonEnabled;
  const roleMissing = !isBeta;
  const blocked = featureOff || roleMissing;

  useEffect(() => {
    if (blocked || !codeLooksValid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${env.qgammonWsUrl}/invites/${encodeURIComponent(code)}`, {
          headers: await qgammonAuthHeaders(),
        });
        if (cancelled) return;
        if (res.status === 404) {
          setRefusal('expired');
          return;
        }
        if (!res.ok) throw new Error(`preview failed: ${res.status}`);
        setPreview((await res.json()) as InvitePreview);
      } catch {
        if (!cancelled) setRefusal('unreachable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blocked, code, codeLooksValid]);

  const accept = useCallback(async () => {
    setAccepting(true);
    setRefusal(null);
    try {
      const res = await fetch(`${env.qgammonWsUrl}/invites/${encodeURIComponent(code)}/accept`, {
        method: 'POST',
        headers: await qgammonAuthHeaders(true),
        body: JSON.stringify({}),
      });
      if (res.status === 400) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setRefusal(body.error === 'cannot_accept_own' ? 'own' : 'unreachable');
        return;
      }
      if (res.status === 404) {
        // La preview era andata a buon fine un istante fa: se l'invito ora non
        // c'e' piu' e aveva ancora tempo, la scadenza NON e' la causa.
        setRefusal(refusalFor404(preview, Date.now()));
        return;
      }
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; who?: string };
        setRefusal(
          body.error !== 'too_many_active_matches'
            ? 'unreachable'
            : body.who === 'creator'
              ? 'capCreator'
              : 'capYou',
        );
        return;
      }
      if (!res.ok) throw new Error(`accept failed: ${res.status}`);
      const body = (await res.json()) as { matchId: string; color: Player };
      haptics.notification('success');
      startMatch({ game: 'qgammon', matchId: body.matchId, perspective: body.color });
    } catch {
      setRefusal('unreachable');
    } finally {
      setAccepting(false);
    }
  }, [code, haptics, startMatch, preview]);

  // ── Cosa si rende, in ordine di precedenza ──────────────────────────────
  // L'ordine conta: un codice malformato non e' «scaduto», e una build senza la
  // sezione non e' «invito non valido». Ogni ramo nomina la SUA causa.

  if (!codeLooksValid) {
    return <Notice title={t('invite.landing.malformedTitle')} body={t('invite.landing.malformedBody')} onHome={() => navigate('/')} homeLabel={t('invite.landing.goHome')} />;
  }
  if (featureOff) {
    return <Notice title={t('invite.landing.unavailableTitle')} body={t('invite.landing.unavailableBody')} onHome={() => navigate('/')} homeLabel={t('invite.landing.goHome')} />;
  }
  if (roleMissing) {
    return <Notice title={t('invite.landing.betaTitle')} body={t('invite.landing.betaBody')} onHome={() => navigate('/')} homeLabel={t('invite.landing.goHome')} />;
  }
  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="invite-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  const REFUSAL_COPY: Record<Refusal, { title: string; body: string }> = {
    expired: { title: t('invite.landing.expiredTitle'), body: t('invite.landing.expiredBody') },
    gone: { title: t('invite.landing.goneTitle'), body: t('invite.landing.goneBody') },
    own: { title: t('invite.landing.ownTitle'), body: t('invite.landing.ownBody') },
    capYou: { title: t('invite.landing.capYouTitle'), body: t('invite.landing.capYouBody') },
    capCreator: { title: t('invite.landing.capCreatorTitle'), body: t('invite.landing.capCreatorBody') },
    unreachable: { title: t('invite.landing.unreachableTitle'), body: t('invite.landing.unreachableBody') },
  };

  if (refusal) {
    const copy = REFUSAL_COPY[refusal];
    return (
      <Notice
        title={copy.title}
        body={copy.body}
        onHome={() => navigate('/')}
        homeLabel={t('invite.landing.goHome')}
        onRetry={refusal === 'unreachable' ? () => window.location.reload() : undefined}
        retryLabel={t('invite.landing.retry')}
      />
    );
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6 p-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <KeyRound className="h-6 w-6 text-brand" />
        <h1 className="text-lg font-bold text-fg">{t('invite.landing.title')}</h1>
        {preview && (
          <p className="text-sm text-fg-muted">
            {t('invite.preview', {
              name: preview.creator.displayName || preview.creator.username,
              elo: preview.creator.elo,
              length: preview.matchLength,
            })}
          </p>
        )}
      </div>
      <button
        type="button"
        disabled={accepting}
        onClick={() => void accept()}
        className="rounded-md bg-brand px-8 py-3 text-base font-medium text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
      >
        {accepting ? t('invite.creating') : t('invite.accept')}
      </button>
    </div>
  );
}

/**
 * Un esito che non e' una partita. Ha sempre un modo di uscire: chi arriva da un
 * link puo' non aver mai visto QLASH, e lasciarlo su una frase senza azione e'
 * il vicolo cieco che il deep link doveva togliere, non aggiungere.
 */
function Notice({
  title,
  body,
  onHome,
  homeLabel,
  onRetry,
  retryLabel,
}: {
  title: string;
  body: string;
  onHome: () => void;
  homeLabel: string;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-lg font-bold text-fg">{title}</h1>
      <p className="max-w-sm text-sm text-fg-muted">{body}</p>
      <div className="flex gap-3">
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-md border border-border px-5 py-2 text-sm text-fg"
          >
            {retryLabel}
          </button>
        )}
        <button
          type="button"
          onClick={onHome}
          className="rounded-md bg-brand px-5 py-2 text-sm font-medium text-brand-fg"
        >
          {homeLabel}
        </button>
      </div>
    </div>
  );
}
